"use client";

// ============================================================
// CLUBHUB – /gruppen: der Gruppenbaum mit Filterkopf
// ============================================================
// Aufbau wie jede Liste (`components/liste`): Kopf, EINE Karte mit
// Filterkopf, Baum und Summenzeile. Die Ansicht (Suche, Art, Archiv,
// Sortierung) steht in der Adresse; gefiltert wird hier auf dem
// Bestand im Browser, damit Angelegtes sofort mitzaehlt.
// BACKEND: Filtern und Zaehlen wandern auf den Server (gleiche Schluessel).
//
// Jede Dimension zaehlt ueber alles AUSSER sich selbst — die Zahl am
// Knopf „Mannschaft 5“ sagt, was man bekommt, wenn man ihn drueckt.
// ============================================================
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Plus, UsersRound } from "lucide-react";
import { Button, EmptyState, PageHeader } from "@/components/ui";
import {
  Filterkopf,
  Filterloesen,
  Filterzurueck,
  Fusstext,
  Listenblatt,
  Listenfuss,
  Listenkoerper,
  Listenseite,
  Luecke,
  Schiene,
  Suchfeld,
  useAnsicht,
  Zaehlknopf,
  Zaehlschiene,
} from "@/components/liste";
import { GroupKind } from "@/generated/prisma/enums";
import { GROUP_KIND } from "@/lib/labels";
import { GRUPPEN_PARAMS, gruppenFilterZahl, type GruppenAnsicht } from "@/lib/gruppen/ansicht";
import { aktuellePersonen, baumZeilen, pfadText, type BaumZeile } from "@/lib/gruppen/logik";
import type { GruppeView } from "@/lib/gruppen/views";
import { sortRows } from "@/lib/sorting";
import { formatDecimal } from "@/lib/number-format";
import { useGruppenBestand } from "./gruppen-bestand";
import { GruppenBaum, useGruppenKennzahlen } from "./gruppen-baum";
import { GruppeDialog } from "./gruppe-dialog";

type Dimension = "art" | "zustand";

export function GruppenListe({ ansicht }: { ansicht: GruppenAnsicht }) {
  const { gruppen, zugehoerigkeiten, heute, rechte } = useGruppenBestand();
  const { zahl } = useGruppenKennzahlen();
  const { setzen, isPending } = useAnsicht();
  const router = useRouter();
  const [dialog, setDialog] = useState<null | { gruppe?: GruppeView; eltern?: string | null }>(null);

  const nadel = ansicht.q.toLowerCase();

  const passt = useMemo(() => {
    const heuhaufen = (g: GruppeView) =>
      [g.name, g.description, g.emailAlias, g.sportCode, pfadText(gruppen, g.id)].filter(Boolean).join(" ").toLowerCase();
    return (g: GruppeView, ausser?: Dimension) => {
      if (ausser !== "zustand" && g.isArchived !== ansicht.archiv) return false;
      if (ausser !== "art" && ansicht.art && g.kind !== ansicht.art) return false;
      if (nadel && !heuhaufen(g).includes(nadel)) return false;
      return true;
    };
  }, [gruppen, ansicht.archiv, ansicht.art, nadel]);

  const treffer = gruppen.filter((g) => passt(g));
  const artZaehler = Object.fromEntries(
    Object.values(GroupKind).map((k) => [k, gruppen.filter((g) => passt(g, "art") && g.kind === k).length]),
  ) as Record<GroupKind, number>;
  const fuerZustand = gruppen.filter((g) => passt(g, "zustand"));
  const zustandZaehler = { aktiv: fuerZustand.filter((g) => !g.isArchived).length, archiv: fuerZustand.filter((g) => g.isArchived).length };

  const ordnen = (geschwister: GruppeView[]) =>
    sortRows(
      geschwister,
      ansicht.sort,
      {
        gruppe: (g) => g.name,
        art: (g) => GROUP_KIND[g.kind],
        mitglieder: (g) => zahl.get(g.id)?.gesamt ?? 0,
      },
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "de"),
    );

  // Aktiv: der Baum (Treffer mit ihrem Weg). Archiv: flach, mit Weg als Zeile.
  const zeilen: BaumZeile[] = ansicht.archiv
    ? ordnen(treffer).map((g) => ({ gruppe: g, tiefe: 0, hatKinder: false, nurPfad: false }))
    : baumZeilen(
        gruppen.filter((g) => !g.isArchived),
        new Set(treffer.map((g) => g.id)),
        ordnen,
      );

  const personen = aktuellePersonen(zugehoerigkeiten, treffer.map((g) => g.id), heute).size;
  const ohneMail = treffer.filter((g) => !g.emailAlias).length;
  const filterZahl = gruppenFilterZahl(ansicht);
  const aktiveGesamt = gruppen.filter((g) => !g.isArchived).length;

  const arten = Object.values(GroupKind).filter((k) => artZaehler[k] > 0 || ansicht.art === k);

  return (
    <Listenseite>
      <PageHeader
        title="Gruppen"
        description={
          ansicht.archiv
            ? `${formatDecimal(treffer.length)} archivierte Gruppen`
            : `${treffer.length === aktiveGesamt ? formatDecimal(aktiveGesamt) : `${formatDecimal(treffer.length)} von ${formatDecimal(aktiveGesamt)}`} Gruppen · ${formatDecimal(personen)} Personen darin`
        }
        actions={
          rechte.schreiben ? (
            <Button onClick={() => setDialog({})}>
              <Plus className="h-4 w-4" aria-hidden />
              Neue Gruppe
            </Button>
          ) : undefined
        }
      />

      <Listenblatt>
        <Filterkopf
          isPending={isPending}
          oben={
            <>
              <Suchfeld wert={ansicht.q} platzhalter="Name, Mailadresse, Sportart …" beschriftung="Gruppen durchsuchen" />
              <Luecke />
              <Zaehlschiene titel="Art">
                {arten.map((k) => (
                  <Zaehlknopf
                    key={k}
                    label={GROUP_KIND[k]}
                    anzahl={artZaehler[k]}
                    aktiv={ansicht.art === k}
                    onClick={() => setzen({ art: ansicht.art === k ? null : k.toLowerCase() })}
                  />
                ))}
              </Zaehlschiene>
              <Schiene>
                <Zaehlknopf
                  label="Aktiv"
                  anzahl={zustandZaehler.aktiv}
                  aktiv={!ansicht.archiv}
                  titel="Gruppen im Vereinsleben"
                  onClick={() => setzen({ zustand: null })}
                />
                <Zaehlknopf
                  label="Archiv"
                  anzahl={zustandZaehler.archiv}
                  aktiv={ansicht.archiv}
                  titel="Aufgelöste Gruppen — mit Mitgliedern und Historie"
                  onClick={() => setzen({ zustand: ansicht.archiv ? null : "archiv" })}
                />
              </Schiene>
            </>
          }
          unten={<Filterzurueck anzahl={filterZahl} params={GRUPPEN_PARAMS} />}
        />

        {zeilen.length === 0 ? (
          <Listenkoerper className="p-5">
            {filterZahl > 0 ? (
              <EmptyState
                icon={UsersRound}
                title={ansicht.archiv && !nadel && !ansicht.art ? "Das Archiv ist leer" : "Keine Gruppe passt zu diesem Filter"}
                description={ansicht.archiv ? "Archivierte Gruppen erscheinen hier — mit ihren früheren Mitgliedern." : "Ändere die Suche oder setze die Filter zurück."}
                action={<Filterloesen params={GRUPPEN_PARAMS} />}
              />
            ) : (
              <EmptyState
                icon={UsersRound}
                title="Noch keine Gruppen"
                description="Lege die erste Gruppe an — zum Beispiel den Vorstand oder eine Mannschaft. Mitglieder ordnest du danach zu."
                action={
                  rechte.schreiben ? (
                    <Button onClick={() => setDialog({})}>
                      <Plus className="h-4 w-4" aria-hidden />
                      Neue Gruppe
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Listenkoerper>
        ) : (
          <GruppenBaum
            zeilen={zeilen}
            sort={ansicht.sort}
            zeigePfad={ansicht.archiv}
            onBearbeiten={(g) => setDialog({ gruppe: g })}
            onUntergruppe={(g) => setDialog({ eltern: g.id })}
          />
        )}

        <Listenfuss>
          <Fusstext titel="Summe" zusatz={`${formatDecimal(treffer.length)} ${treffer.length === 1 ? "Gruppe" : "Gruppen"}`} />
          <span className="text-body-sm text-text-secondary">
            Personen <span className="numeric text-text-primary font-medium">{formatDecimal(personen)}</span>
          </span>
          {ohneMail > 0 && !ansicht.archiv && (
            <span className="text-body-sm text-text-secondary">
              Ohne Vereins-Mail <span className="numeric text-text-primary font-medium">{ohneMail}</span>
            </span>
          )}
        </Listenfuss>
      </Listenblatt>

      <GruppeDialog
        offen={dialog !== null}
        onClose={() => setDialog(null)}
        gruppe={dialog?.gruppe}
        vorgabeEltern={dialog?.eltern}
        onGespeichert={(g) => {
          if (!dialog?.gruppe) router.push(`/gruppen/${g.id}`);
        }}
      />
    </Listenseite>
  );
}
